import { useCallback } from 'react';
import { cacheModify, useApolloClient } from '@frameio/graphql-client';
import type {
  DuplicateShareInput,
  DuplicateShareMutationOptions,
  Project,
  ShareNameFragment,
} from '@frameio/graphql-client/generated';
import {
  ShareName,
  useDuplicateShareMutation,
} from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { EMPTY_OBJECT } from '@frameio/utils';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import TEXT from './generated/TEXT/useDuplicateShare';

TEXT.DEFINE({
  /** A message to display within a toastwhen an error occurs during the attempted duplication */
  labelErrorToast: (name: string) =>
    `There was an issue attempting to duplicate "${name}"`,
  /** A message to display within a toas twhen the duplication is successful */
  labelSuccessToast: (name: string) => `Duplicated "${name}"`,
});

type DuplicateShareArgs = Pick<DuplicateShareInput, 'shareId'>;

export const useDuplicateShare = (
  options: Omit<DuplicateShareMutationOptions, 'variables'> = EMPTY_OBJECT
) => {
  const [duplicateShareMutation] = useDuplicateShareMutation();
  const client = useApolloClient();
  const projectId = useActiveProjectId();
  const tryCatchDuplicate = useToastTryCatchHandler();

  const duplicateShare = useCallback(
    ({ shareId }: DuplicateShareArgs) => {
      const shareInfo = client.readFragment<ShareNameFragment>({
        fragment: ShareName,
        fragmentName: 'ShareName',
        id: client.cache.identify({ __typename: 'Share', id: shareId }),
      });

      const shareName = shareInfo?.name;

      const duplicate = () =>
        duplicateShareMutation({
          variables: { shareId },
          update: (cache, { data }) => {
            cacheModify<Project>(cache, {
              id: cache.identify({
                __typename: 'Project',
                id: projectId,
              }),
              fields: {
                shares: (value, { toReference }) => {
                  if (value.nodes) {
                    return {
                      ...value,
                      nodes: [
                        toReference(data.duplicateShare.share),
                        ...value.nodes,
                      ],
                      totalCount: value.totalCount + 1,
                    };
                  }
                  return value;
                },
              },
            });
          },
          ...options,
        });

      if (shareName) {
        tryCatchDuplicate({
          attempt: duplicate,
          successMessage: TEXT.labelSuccessToast(shareName)?.toString(),
          errorMessage: TEXT.labelErrorToast(shareName)?.toString(),
        });
      }
    },
    [client, duplicateShareMutation, options, projectId, tryCatchDuplicate]
  );

  return duplicateShare;
};
